import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  emptyFiltersParam,
  emptyFiltersValue,
  getFilterValuesFromSearchParams,
  getSearchParamsFromFilterValues,
  hasDefaultFilterValues,
} from './utils';

const useDataViewFilters = ({
  fields = [],
  onApply,
  searchParams,
  setSearchParams,
}) => {
  const paramsKey = searchParams?.toString() || '';
  const onApplyRef = useRef(onApply);
  const skipNextUrlSync = useRef(false);
  const mounted = useRef(false);

  const defaultValues = useMemo(
    () => getFilterValuesFromSearchParams(fields, searchParams),
    [fields, paramsKey]
  );

  const form = useForm({
    defaultValues,
  });

  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }

    form.reset(defaultValues);

    if (onApplyRef.current) {
      onApplyRef.current({ page: 0, filter: defaultValues });
    }
  }, [paramsKey]);

  useEffect(() => {
    if (!setSearchParams || paramsKey) {
      return;
    }

    const nextSearchParams = getSearchParamsFromFilterValues(
      fields,
      defaultValues
    );

    if (!nextSearchParams.toString()) {
      return;
    }

    skipNextUrlSync.current = true;
    setSearchParams(nextSearchParams, {
      replace: true,
    });
  }, [defaultValues, fields, paramsKey, setSearchParams]);

  const applyFilter = (props = {}) => {
    const filter = form.getValues();

    if (setSearchParams) {
      const nextSearchParams = getSearchParamsFromFilterValues(fields, filter);

      if (!nextSearchParams.toString() && hasDefaultFilterValues(fields)) {
        nextSearchParams.set(emptyFiltersParam, emptyFiltersValue);
      }

      skipNextUrlSync.current = nextSearchParams.toString() !== paramsKey;
      setSearchParams(nextSearchParams, {
        replace: true,
      });
    }

    if (onApplyRef.current) {
      onApplyRef.current({ page: 0, ...props, filter });
    }
  };

  return {
    applyFilter,
    filterForm: form,
  };
};

export default useDataViewFilters;
